import type {
  MeetingAnnouncementView,
  MeetingAnnouncementPhotoView,
  UserPublicProfileView,
} from '../types';
import type { Profile } from '@/data/profiles';
import type { Announcement } from '@/data/announcementsData';

// Meeting type ID to category name mapping (hardcoded as API doesn't provide endpoint)
const MEETING_TYPE_MAP: Record<number, string> = {
  1: 'Kawa',
  2: 'Spacer',
  3: 'Muzeum',
  4: 'Kolacja',
  5: 'Zakupy',
  6: 'Film',
  7: 'Sport',
  8: 'Kultura',
  9: 'Kulinaria',
};

// Status code to status mapping
const STATUS_MAP: Record<string, Announcement['status']> = {
  active: 'active',
  completed: 'completed',
  cancelled: 'cancelled',
  pending: 'active', // treat pending as active for display
};

/**
 * Format date from API (YYYY-MM-DD) to display format (DD miesiąca)
 */
export const formatDisplayDate = (dateStr: string): string => {
  const months = [
    'stycznia',
    'lutego',
    'marca',
    'kwietnia',
    'maja',
    'czerwca',
    'lipca',
    'sierpnia',
    'września',
    'października',
    'listopada',
    'grudnia',
  ];

  try {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = months[date.getMonth()];
    return `${day} ${month}`;
  } catch {
    return dateStr;
  }
};

/**
 * Format time from API (HH:MM:SS) to display format (HH:MM)
 */
export const formatDisplayTime = (timeStr: string): string => {
  if (!timeStr) return '';
  // Handle both HH:MM:SS and HH:MM formats
  const parts = timeStr.split(':');
  return `${parts[0]}:${parts[1]}`;
};

/**
 * Get category name from meeting type ID
 */
export const getCategoryFromTypeId = (
  typeId: number | null | undefined,
): string => {
  if (!typeId) return 'Inne';
  return MEETING_TYPE_MAP[typeId] ?? 'Inne';
};

/**
 * Get announcement status from status code
 */
export const getStatusFromCode = (
  statusCode: string | null | undefined,
): Announcement['status'] => {
  if (!statusCode) return 'active';
  return STATUS_MAP[statusCode.toLowerCase()] ?? 'active';
};

/**
 * Format age range for display
 */
export const formatAgeRange = (
  ageFrom: number | null,
  ageTo: number | null,
): string => {
  if (ageFrom && ageTo) {
    return `${ageFrom}-${ageTo}`;
  }
  if (ageFrom) {
    return `${ageFrom}+`;
  }
  if (ageTo) {
    return `do ${ageTo}`;
  }
  return '';
};

/**
 * Extract photo URLs from announcement photos
 */
export const extractPhotoUrls = (
  photos: MeetingAnnouncementPhotoView[],
): string[] => {
  if (!photos || photos.length === 0) return [];

  // Sort by isDefault (default first), then by createdAt
  const sorted = [...photos].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  return sorted.map(p => p.downloadUrl);
};

/**
 * Get default photo URL from announcement or photos
 */
export const getDefaultPhotoUrl = (
  announcement: MeetingAnnouncementView,
  photos?: MeetingAnnouncementPhotoView[],
): string => {
  // First try to get from photos array
  if (photos && photos.length > 0) {
    const defaultPhoto = photos.find(p => p.isDefault);
    if (defaultPhoto) return defaultPhoto.downloadUrl;
    return photos[0].downloadUrl;
  }

  // Fallback to creator's thumbnail
  if (announcement.createdByPhotoThumbnail) {
    return announcement.createdByPhotoThumbnail;
  }

  // Final fallback
  return 'https://static.photos/people/1200x630/1';
};

/**
 * Map MeetingAnnouncementView to Profile type for SwipeCard
 * @param announcement - The announcement data
 * @param photos - Optional announcement photos (deprecated, use announcement.photos)
 * @param userProfile - Optional user public profile (for age, verification status)
 */
export const mapAnnouncementToProfile = (
  announcement: MeetingAnnouncementView,
  photos?: MeetingAnnouncementPhotoView[],
  userProfile?: UserPublicProfileView,
): Profile => {
  // Use photos from announcement object first, then fallback to passed photos
  const announcementPhotos = announcement.photos ?? photos ?? [];
  const photoUrls = extractPhotoUrls(announcementPhotos);

  // Use announcement photos as primary, fallback to user's profile photo
  const primaryPhotoUrl =
    announcementPhotos.length > 0
      ? (announcementPhotos.find(p => p.isDefault) || announcementPhotos[0]).downloadUrl
      : announcement.createdByPhotoThumbnail ||
        'https://static.photos/people/1200x630/1';

  // Build display name - prefer first name over nickname
  const displayName =
    announcement.createdByFirstName ||
    announcement.createdByNickname ||
    'Anonim';

  // Build tagline from additional info or address
  const tagline = announcement.additionalInfo || announcement.meetingAddress;

  // Format date and time for display
  const dateLabel = `${formatDisplayDate(
    announcement.meetingDate,
  )} ${formatDisplayTime(announcement.meetingTime)}`;

  // Format price
  const priceLabel = announcement.paymentAmount
    ? `${announcement.paymentAmount} zł`
    : announcement.coversAllCosts
    ? 'Pokrywa koszty'
    : '';

  // Get age from user profile if available, otherwise use ageFrom from announcement
  const age = userProfile?.age ?? announcement.ageFrom ?? 0;

  // Get verification status from user profile
  const verified = userProfile?.isVerified ?? false;

  // Format rating from user profile
  const rating =
    userProfile?.ratingAverage && userProfile.ratingAverage > 0
      ? userProfile.ratingAverage.toFixed(1)
      : 'Brak oceny';

  return {
    id: announcement.id,
    name: displayName,
    age,
    verified,
    onlineStatus: 'online', // Default, API doesn't provide online status
    tagline,
    dateLabel,
    priceLabel,
    distanceKm: '', // Would need geolocation calculation
    rating,
    photos: photoUrls.length > 0 ? photoUrls : undefined,
    photoUrl: primaryPhotoUrl, // Use createdByPhotoThumbnail as primary photo
  };
};

/**
 * Application counts for an announcement
 */
export interface AnnouncementApplicationCounts {
  totalApplications: number;
  confirmedApplications: number;
}

/**
 * Map MeetingAnnouncementView to Announcement type for MyAnnouncementsScreen
 * @param announcement - The announcement data
 * @param applicationCounts - Optional application counts (total and confirmed)
 */
export const mapAnnouncementToCard = (
  announcement: MeetingAnnouncementView,
  applicationCounts?: AnnouncementApplicationCounts,
): Announcement => {
  return {
    id: announcement.id,
    title:
      announcement.additionalInfo?.substring(0, 50) ||
      getCategoryFromTypeId(announcement.meetingTypeId),
    description: announcement.additionalInfo || 'Brak opisu',
    category: getCategoryFromTypeId(announcement.meetingTypeId),
    date: formatDisplayDate(announcement.meetingDate),
    time: formatDisplayTime(announcement.meetingTime),
    location: announcement.meetingAddress,
    participants: 0, // API doesn't provide current participants count directly
    maxParticipants: announcement.participantCount,
    likes: applicationCounts?.totalApplications ?? 0, // Total applications count
    comments: applicationCounts?.confirmedApplications ?? 0, // Confirmed applications count
    status: getStatusFromCode(announcement.statusCode),
  };
};

/**
 * Check if announcement is active
 */
export const isAnnouncementActive = (
  announcement: MeetingAnnouncementView,
): boolean => {
  const status = getStatusFromCode(announcement.statusCode);
  return status === 'active';
};

/**
 * Check if announcement is archived (completed or cancelled)
 */
export const isAnnouncementArchived = (
  announcement: MeetingAnnouncementView,
): boolean => {
  const status = getStatusFromCode(announcement.statusCode);
  return status === 'completed' || status === 'cancelled';
};
